import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

export default function CandidatesPage() {
  const { pharmacy, role, user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newC, setNewC] = useState({ name: '', note: '' });

  const canEdit = ['admin', 'pharmacist', 'superadmin'].includes(role);

  const load = async () => {
    if (!pharmacy) return;
    setLoading(true);
    const snap = await getDocs(
      query(collection(db, 'pharmacies', pharmacy.id, 'patients'),
        where('status', '==', 'candidate'))
    );
    setCandidates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  useEffect(() => { load(); }, [pharmacy]);

  const enroll = async (p) => {
    await updateDoc(doc(db, 'pharmacies', pharmacy.id, 'patients', p.id), {
      status: 'active', cycleLength: 28, ready: false,
      ignored: false, updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: user.email,
      updatedByRole: role,
    });
    load();
  };

  const addCandidate = async () => {
    if (!newC.name.trim()) return;
    await addDoc(collection(db, 'pharmacies', pharmacy.id, 'patients'), {
      name: newC.name.trim(), note: newC.note.trim(),
      status: 'candidate', ready: false, ignored: false,
      deliveryPatient: false, createdAt: serverTimestamp(), updatedAt: serverTimestamp()
    });
    setNewC({ name: '', note: '' });
    setShowAdd(false);
    load();
  };

  return (
    <div style={{ padding: '28px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0F172A' }}>Candidates</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94A3B8' }}>Patients not yet enrolled in blister pack program</p>
        </div>
        {canEdit && (
          <button onClick={() => setShowAdd(true)} style={{
            padding: '9px 20px', background: '#0F172A', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer'
          }}>+ Add Candidate</button>
        )}
      </div>

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 380,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700 }}>Add Candidate</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Name</label>
              <input value={newC.name} onChange={e => setNewC({ ...newC, name: e.target.value })}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
                  borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 5 }}>Note</label>
              <input value={newC.note} onChange={e => setNewC({ ...newC, note: e.target.value })}
                placeholder="Optional"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB',
                  borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={addCandidate} style={{ flex: 1, padding: '10px', background: '#0F172A',
                color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
                Add
              </button>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px',
                background: '#F1F5F9', color: '#0F172A', border: 'none', borderRadius: 8,
                fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : candidates.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94A3B8' }}>
            No candidates yet. Add patients you're considering for the blister pack program.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8FAFC' }}>
                {['Name', 'Note', 'Added', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #F1F5F9' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {candidates.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #F8FAFC' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0F172A' }}>{c.name}</td>
                  <td style={{ padding: '12px 16px', color: '#64748B' }}>{c.note || '—'}</td>
                  <td style={{ padding: '12px 16px', color: '#64748B' }}>
                    {c.createdAt?.toDate ? c.createdAt.toDate().toISOString().slice(0,10) : '—'}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {canEdit && (
                      <button onClick={() => enroll(c)} style={{
                        padding: '6px 14px', background: '#0F172A', color: '#fff',
                        border: 'none', borderRadius: 8, fontSize: 12, cursor: 'pointer'
                      }}>Enroll</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
